import { Empty, IconFont, Input, Modal, Radio, RadioGroup, Toast } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { WorkflowType } from '@lighthouse/shared'
import { useAtomAction, useAtomAsyncAction, WorkflowTextMap } from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { addWorkflowAtom, deleteWorkflowAtom, fetchWorkflowListAtom } from '@/atoms/workflow/action'
import { workflowListAtom } from '@/atoms/workflow/state'
import * as srv from '@/services'

// import { getNodesWithPosition } from '../FlowDetail/utils/getNodesPosition'
import { FlowCard } from './components/FlowCard'

interface FlowListProps {
    type: WorkflowType | 'all'
    onCardClick?: (id: string) => void
}

const SCxAllFlowWrapper = styled.div``

const SCxAllFlowHeader = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    padding-top: 20px;
    padding-bottom: 12px;
    background-color: var(--color-gray-50);
`

const SCxAllFlowTitle = styled.h2`
    font-size: 20px;
    line-height: 40px;
    white-space: nowrap;
`

const SCxAllFlowOperatorWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`

const SCxAllFlowCardWrapper = styled.div`
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(auto-fill, minmax(367px, 1fr));
    padding-bottom: 20px;
`

export const AllFlow: React.FC<FlowListProps> = ({ type, onCardClick }) => {
    const workflows = useAtomValue(workflowListAtom)
    const { run: updateWorkflow } = useAtomAsyncAction(fetchWorkflowListAtom)
    const { run: deleteWorkflow } = useAtomAction(deleteWorkflowAtom)
    const { run: addWorkflow } = useAtomAction(addWorkflowAtom)
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('all')
    // const [type, setType] = useState('all')

    // useMount(() => {
    //     updateWorkflow()
    // })

    const handleFlowDelete = useCallback(
        async (id: string) => {
            const isConfirm = await Modal.confirm({
                title: '确认删除',
                content: '确认删除工作流？',
                okStatus: 'error'
            })
            if (isConfirm) {
                const isSuccess = await srv.deleteFlow(id)
                if (isSuccess) {
                    deleteWorkflow(id)
                    Toast.success('删除成功')
                } else {
                    Toast.error('删除失败')
                }
            }
        },
        [deleteWorkflow]
    )

    const handleFlowDuplicate = useCallback(
        async (id: string) => {
            try {
                const newWorkflow = await srv.duplicateFlow(id)
                if (newWorkflow) {
                    addWorkflow(newWorkflow)
                    Toast.success('复制成功')
                } else {
                    Toast.error('复制失败')
                }
            } catch {
                Toast.error('复制失败，请稍后重试')
            }
        },
        [addWorkflow]
    )

    const flowsContent = useMemo(() => {
        const searchValue = search.trim().toLowerCase()
        const currentFlows = workflows.filter(flow => {
            // 状态过滤
            if (status === 'enabled' && !flow.enabled) {
                return false
            }
            if (status === 'disabled' && flow.enabled) {
                return false
            }

            // 类型过滤
            if ((type === 'automation' || type === 'action' || type === 'subProcess') && flow.type !== type) {
                return false
            }

            // 搜索过滤
            return !(searchValue && !flow.name.toLowerCase().includes(searchValue))
        })

        if (currentFlows.length === 0) {
            const imgUrl = getAssetUrl('empty', 'no_flow.svg')
            return (
                <Empty
                    styles={{ root: { marginTop: '100px', col: 3 } }}
                    icon={<img style={{ width: 150, marginBottom: 4 }} src={imgUrl} alt="no flow" />}
                    description="没有流程"
                />
            )
        }
        return (
            <SCxAllFlowCardWrapper>
                {currentFlows.map(flow => {
                    const { id: flowId } = flow
                    return (
                        <FlowCard
                            key={flowId}
                            id={flowId}
                            onClick={() => {
                                onCardClick?.(flowId)
                            }}
                            onDuplicate={() => handleFlowDuplicate(flowId)}
                            onDelete={() => handleFlowDelete(flowId)}
                        />
                    )
                })}
            </SCxAllFlowCardWrapper>
        )
    }, [handleFlowDelete, handleFlowDuplicate, onCardClick, status, type, workflows, search])

    return (
        <SCxAllFlowWrapper>
            <SCxAllFlowHeader>
                <SCxAllFlowTitle>{type === 'all' ? '所有流程' : WorkflowTextMap[type]}</SCxAllFlowTitle>
                <SCxAllFlowOperatorWrapper>
                    <RadioGroup
                        name="type"
                        styles={{
                            root: {
                                marginLeft: '18px',
                                gap: 12,
                                flexWrap: 'nowrap'
                            }
                        }}
                        value={status}
                        onChange={values => {
                            setStatus(values)
                        }}
                    >
                        <Radio styles={{ label: { whiteSpace: 'nowrap' } }} size="xs" value="all" label="全部" />
                        <Radio styles={{ label: { whiteSpace: 'nowrap' } }} size="xs" value="enabled" label="已启用" />
                        <Radio styles={{ label: { whiteSpace: 'nowrap' } }} size="xs" value="disabled" label="未启用" />
                    </RadioGroup>
                    <Input
                        prefix={<IconFont type="Search" color="var(--color-gray-400)" />}
                        value={search}
                        placeholder="搜索"
                        size="md"
                        clearable
                        onClear={() => setSearch('')}
                        onChange={e => setSearch?.(e.currentTarget.value)}
                        styles={{
                            wrapper: {
                                borderRadius: 5
                            }
                        }}
                        style={{
                            width: 174
                        }}
                    />
                </SCxAllFlowOperatorWrapper>
            </SCxAllFlowHeader>
            {flowsContent}
        </SCxAllFlowWrapper>
    )
}
